import type {
  RoomDef,
  PuzzleDef,
  PuzzleParams,
  PuzzleInstanceState,
  EscapeStateV2,
} from './types';

// ── Status ─────────────────────────────────────────────────────────────────────

export interface PuzzleStatus {
  solved: boolean;
  locked: boolean;
  available: boolean;
}

export function getPuzzleStatus(
  puzzleId: string,
  rooms: RoomDef[],
  state: EscapeStateV2,
): PuzzleStatus {
  let def: PuzzleDef | undefined;
  for (const room of rooms) {
    def = room.puzzles.find(p => p.id === puzzleId);
    if (def) break;
  }
  if (!def) return { solved: false, locked: true, available: false };

  const instanceState = state.puzzleState[puzzleId];
  const solved = instanceState?.solved ?? false;

  const dependsSatisfied = (def.dependsOn ?? []).every(
    depId => state.puzzleState[depId]?.solved ?? false,
  );
  const inventorySatisfied = (def.dependsOnInventory ?? []).every(
    itemId => state.inventory.includes(itemId),
  );

  const locked = !dependsSatisfied || !inventorySatisfied;
  return { solved, locked, available: !locked && !solved };
}

// ── Room completion ────────────────────────────────────────────────────────────

export function isRoomComplete(
  roomIndex: number,
  rooms: RoomDef[],
  state: EscapeStateV2,
): boolean {
  const room = rooms[roomIndex];
  if (!room) return false;

  const rule = room.completionRule;

  if (rule.type === 'all') {
    return room.puzzles.every(p => state.puzzleState[p.id]?.solved ?? false);
  }

  if (rule.type === 'required-plus-min') {
    const requiredSolved = rule.required.every(
      id => state.puzzleState[id]?.solved ?? false,
    );
    const totalSolved = room.puzzles.filter(
      p => state.puzzleState[p.id]?.solved ?? false,
    ).length;
    return requiredSolved && totalSolved >= rule.minTotal;
  }

  return false;
}

// ── Default states ─────────────────────────────────────────────────────────────

export function getDefaultPuzzleState(params: PuzzleParams): PuzzleInstanceState {
  switch (params.type) {
    case 'hotspot-scavenger':
      return { type: 'hotspot-scavenger', solved: false, visitedIds: [] };
    case 'pattern-match':
      return { type: 'pattern-match', solved: false, selected: [], lastWrong: false };
    case 'math-lock':
      return { type: 'math-lock', solved: false, digits: Array(params.digits).fill(''), wrong: false };
    case 'classify-checklist':
      return { type: 'classify-checklist', solved: false, classified: {} };
    case 'd3-scoreboard':
      return {
        type: 'd3-scoreboard',
        solved: false,
        dragCount: 0,
        values: Object.fromEntries(params.metrics.map(m => [m.id, m.initialValue])),
      };
    case 'icon-sequence':
      return { type: 'icon-sequence', solved: false, sequence: Array(params.slots).fill(null), wrong: false };
    case 'frame-question':
      return { type: 'frame-question', solved: false, selectedOptionId: null };
    case 'drag-assembly':
      return { type: 'drag-assembly', solved: false, slotContents: Array(params.slots).fill(null) };
    case 'phrase-assembly':
      return { type: 'phrase-assembly', solved: false, wordSelection: [] };
    case 'final-input':
      return { type: 'final-input', solved: false, inputValue: '', wrong: false };
  }
}

export function initializeMissingPuzzleStates(
  rooms: RoomDef[],
  existing: Record<string, PuzzleInstanceState>,
): Record<string, PuzzleInstanceState> {
  const result: Record<string, PuzzleInstanceState> = { ...existing };
  for (const room of rooms) {
    for (const puzzle of room.puzzles) {
      if (!result[puzzle.id]) {
        result[puzzle.id] = getDefaultPuzzleState(puzzle.params);
      }
    }
  }
  return result;
}
